class FreeGifts extends HTMLElement {
  constructor() {
    super();

    this.list = this.querySelector(".free-gifts-viewport") || this.querySelector(".free-gifts-list");
    this.prevBtn = this.querySelector('button[name="free-gifts-prev"]');
    this.nextBtn = this.querySelector('button[name="free-gifts-next"]');

    this.prevBtn?.addEventListener("click", () => this.scrollByPage(-1));
    this.nextBtn?.addEventListener("click", () => this.scrollByPage(1));
    this.list?.addEventListener("scroll", () => this.updateNav(), { passive: true });
    window.addEventListener("resize", () => this.updateNav());
    window.addEventListener("variant:change", () => this.syncAtcButtons());

    this.querySelectorAll("select[data-free-gift-variant]").forEach((select) => {
      select.addEventListener("change", (event) => this.onVariantChange(event));
      select.addEventListener("click", (event) => event.stopPropagation());
    });

    this.updateNav();
    this.syncAtcButtons();
  }

  getVariantFields() {
    return Array.from(this.querySelectorAll("[data-free-gift-variant]"));
  }

  getGiftVariantIds() {
    return this.getVariantFields()
      .map((field) => Number(field.value))
      .filter((id) => id > 0);
  }

  areSelectionsComplete() {
    const fields = this.getVariantFields();
    if (!fields.length) return true;
    return fields.every((field) => Number(field.value) > 0);
  }

  onVariantChange(event) {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];
    const card = select.closest(".free-gifts-card");
    if (!card || !option) {
      this.syncAtcButtons();
      return;
    }

    const imageSrc = option.dataset.variantImage?.trim();
    const img = card.querySelector(".img-wrapper img");
    if (imageSrc && img) img.src = imageSrc;

    const price = option.dataset.variantPrice?.trim();
    const badge = card.querySelector("[data-free-gift-badge]");
    const compareEl = badge?.querySelector("s");
    if (price && compareEl) {
      compareEl.textContent = price;
    }

    this.syncAtcButtons();
  }

  getAtcButtons() {
    return Array.from(
      document.querySelectorAll(
        'product-form button[name="add"], sticky-atc button[name="add"], product-buy-button button[name="buy"]',
      ),
    );
  }

  syncAtcButtons() {
    const giftReady = this.areSelectionsComplete();
    const chooseText = this.dataset.textChoose;

    this.getAtcButtons().forEach((btn) => {
      const form = btn.closest("form") || btn.closest("product-buy-button")?.closest("form") || btn.closest(".product-form")?.querySelector("form");
      const productForm = btn.closest("product-form") || document.querySelector("product-form");
      const idInput =
        form?.querySelector('[name="id"]') ||
        productForm?.querySelector('[name="id"]');
      const variantSelected = Boolean(idInput?.value);

      if (!giftReady) {
        if (!btn.dataset.textBeforeGifts) {
          btn.dataset.textBeforeGifts = btn.textContent.trim();
        }
        btn.disabled = true;
        if (chooseText) btn.textContent = chooseText;
        btn.setAttribute("data-free-gifts-incomplete", "true");
        return;
      }

      btn.removeAttribute("data-free-gifts-incomplete");

      if (btn.dataset.textBeforeGifts) {
        btn.textContent = btn.dataset.textBeforeGifts;
        delete btn.dataset.textBeforeGifts;
      }

      if (!variantSelected) {
        btn.disabled = true;
        if (btn.dataset.textChoose) btn.textContent = btn.dataset.textChoose;
        return;
      }

      btn.disabled = false;
      if (btn.dataset.textAdd) btn.textContent = btn.dataset.textAdd;
    });

    window.ProductAtcGate?.refresh?.();
  }

  /**
   * Rebuild FormData so free gifts are added first and the main product last.
   * That keeps the main product at the top of the cart (newest / last-added).
   */
  applyToFormData(formData) {
    if (!this.areSelectionsComplete()) return formData;

    const giftIds = this.getGiftVariantIds();
    if (!giftIds.length) return formData;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[")) {
        properties[key.slice(11, -1)] = value;
      }
    }

    const mainId = formData.get("id");
    const mainQty = formData.get("quantity") || 1;
    const sellingPlan = formData.get("selling_plan");

    const existingItems = new Map();
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^items\[([^\]]+)\]\[([^\]]+)\](?:\[([^\]]+)\])?$/);
      if (!match) continue;

      const [, group, field, nested] = match;
      if (!existingItems.has(group)) existingItems.set(group, { properties: {} });
      const item = existingItems.get(group);

      if (nested) {
        item.properties[nested] = value;
      } else {
        item[field] = value;
      }
    }

    formData.delete("id");
    formData.delete("quantity");
    formData.delete("selling_plan");
    Object.keys(properties).forEach((key) => formData.delete(`properties[${key}]`));
    [...formData.keys()].forEach((key) => {
      if (key.startsWith("items[")) formData.delete(key);
    });

    let index = 0;

    giftIds.forEach((id) => {
      formData.append(`items[${index}][id]`, id);
      formData.append(`items[${index}][quantity]`, 1);
      index += 1;
    });

    existingItems.forEach((item) => {
      if (!item.id) return;
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity || 1);
      if (item.selling_plan) {
        formData.append(`items[${index}][selling_plan]`, item.selling_plan);
      }
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, value);
      });
      index += 1;
    });

    if (mainId) {
      formData.append(`items[${index}][id]`, mainId);
      formData.append(`items[${index}][quantity]`, mainQty);
      if (sellingPlan) {
        formData.append(`items[${index}][selling_plan]`, sellingPlan);
      }
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    }

    return formData;
  }

  prependGiftItems(items) {
    if (!this.areSelectionsComplete()) return items;

    const giftIds = this.getGiftVariantIds();
    if (!giftIds.length) return items;

    const giftItems = giftIds.map((id) => ({ id, quantity: 1 }));
    return [...giftItems, ...items];
  }

  focusIncomplete() {
    const incomplete = this.getVariantFields().find((field) => !Number(field.value));
    this.scrollIntoView({ behavior: "smooth", block: "center" });
    incomplete?.focus?.();
  }

  scrollByPage(direction) {
    if (!this.list) return;
    const amount = Math.max(this.list.clientWidth * 0.8, 160);
    this.list.scrollBy({ left: amount * direction, behavior: "smooth" });
  }

  updateNav() {
    if (!this.list || !this.prevBtn || !this.nextBtn) return;

    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const canScroll = maxScroll > 4;

    this.prevBtn.hidden = !canScroll;
    this.nextBtn.hidden = !canScroll;

    if (!canScroll) return;

    this.prevBtn.disabled = this.list.scrollLeft <= 4;
    this.nextBtn.disabled = this.list.scrollLeft >= maxScroll - 4;
  }
}

customElements.define("free-gifts", FreeGifts);
